import React from "react";
import { MessageDescriptor, useIntl } from "react-intl";

// Type for message values
type MessageValues = Record<string, string | number | boolean | null | undefined>;

/**
 * Formats text by converting newlines to React elements with <br /> tags
 * Similar to: text.split("\n").map((line) => (<>{line}<br /></>))
 *
 * @param text - The input string with potential newline characters
 * @returns Array of React elements with line breaks
 */
export function formatText(text: string): React.ReactNode[] {
  if (!text) {
    return [];
  }

  return text.split("\n").map((line, index, array) => {
    const elements: React.ReactNode[] = [line];

    // Add <br /> if not the last line
    if (index < array.length - 1) {
      elements.push(React.createElement("br", { key: `br-${index}` }));
    }

    return React.createElement(React.Fragment, { key: index }, ...elements);
  });
}

/**
 * Hook that provides a function to format translated messages with line breaks
 * Gets the string from formatMessage and then converts newlines to <br /> tags
 *
 * Usage:
 * const formatMessageWithLineBreaks = useFormatMessageWithLineBreaks();
 * const content = formatMessageWithLineBreaks(messages.myMessage, { name: "John" });
 *
 * @returns Function that formats translated messages with line breaks
 */
export function useFormatMessageRich(): (
  messageDescriptor: MessageDescriptor,
  values?: MessageValues
) => React.ReactNode[] {
  const { formatMessage } = useIntl();

  return (messageDescriptor: MessageDescriptor, values?: MessageValues): React.ReactNode[] => {
    const translatedText = formatMessage(messageDescriptor, values);
    return formatText(translatedText);
  };
}
